import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Row, Col, Table, Pagination as BSPagination } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { FiArrowLeft, FiEye, FiPackage, FiDollarSign, FiShoppingCart } from "react-icons/fi";

export default function StockShow() {
    const { t } = useContext(TranslatorContext);
    const { productId } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchProductSales();
    }, [productId]);

    const fetchProductSales = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/stock/${productId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.status) {
                setSales(response.data.sales || []);
                if (response.data.sales && response.data.sales.length > 0) {
                    setProduct({
                        id: response.data.product_id,
                        name: response.data.product_name || `Product #${response.data.product_id}`
                    });
                } else {
                    setProduct({
                        id: response.data.product_id,
                        name: `Product #${response.data.product_id}`
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching product sales:", error);
        } finally {
            setLoading(false);
        }
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentSales = sales.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sales.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Calculate totals
    const totalQuantity = sales.reduce((sum, sale) => sum + (sale.quantity || 0), 0);
    const totalRevenue = sales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);

    const renderPaginationItems = () => {
        let items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <BSPagination.Prev
                key="prev"
                onClick={prevPage}
                disabled={currentPage === 1}
            />
        );

        if (startPage > 1) {
            items.push(<BSPagination.Item key={1} onClick={() => paginate(1)}>1</BSPagination.Item>);
            if (startPage > 2) {
                items.push(<BSPagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <BSPagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => paginate(page)}
                >
                    {page}
                </BSPagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<BSPagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(<BSPagination.Item key={totalPages} onClick={() => paginate(totalPages)}>{totalPages}</BSPagination.Item>);
        }

        items.push(
            <BSPagination.Next
                key="next"
                onClick={nextPage}
                disabled={currentPage === totalPages}
            />
        );

        return items;
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">{t('loading sales data...')}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">
                                {t('Sales History')} - {product?.name || `Product #${productId}`}
                            </h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/stock" className="mc-breadcrumb-link">{t('stock management')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('sales history')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Back Button */}
            <Row className="mb-3">
                <Col>
                    <button 
                        className="btn btn-secondary d-inline-flex align-items-center gap-2"
                        onClick={() => navigate('/stock')}
                    >
                        <FiArrowLeft size={16} />
                        {t('Back to Stock')}
                    </button>
                </Col>
            </Row>

            {/* Sales Table */}
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <h5 className="mb-0">{t('Sales Transactions')}</h5>
                            <span className="badge bg-primary">{sales.length} {t('records')}</span>
                        </div>

                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '15%' }}>{t('Sale Number')}</th>
                                        <th style={{ width: '12%' }}>{t('Date')}</th>
                                        <th style={{ width: '20%' }}>{t('Customer')}</th>
                                        <th className="text-center" style={{ width: '10%' }}>{t('Quantity')}</th>
                                        <th className="text-end" style={{ width: '15%' }}>{t('Total Amount')}</th>
                                        <th className="text-center" style={{ width: '23%' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentSales.length > 0 ? (
                                        currentSales.map((sale, index) => (
                                            <tr key={sale.sale_id}>
                                                <td className="text-center">{indexOfFirstItem + index + 1}</td>
                                                <td>
                                                    <strong >{sale.sale_number}</strong>
                                                </td>
                                                <td>{new Date(sale.sale_date).toLocaleDateString()}</td>
                                                <td>{sale.customer_name || '-'}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-info text-white px-3 py-1">
                                                        {sale.quantity}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <strong className="text-success">
                                                        {parseFloat(sale.total_amount || 0)}
                                                    </strong>
                                                </td>
                                                <td className="text-center">
                                                    <div className="mc-table-action justify-content-center">
                                                        <button
                                                            className="material-icons view"
                                                            onClick={() => navigate(`/sale-edit/${sale.sale_id}`)}
                                                            title={t('View Sale Details')}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <FiEye />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FiShoppingCart size={48} className="text-muted mb-2" />
                                                    <p className="text-muted mb-0">{t('No sales found for this product')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {sales.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-3">
                                <div className="text-muted small">
                                    {t('showing')} {indexOfFirstItem + 1} {t('to')} {Math.min(indexOfLastItem, sales.length)} {t('of')} {sales.length} {t('entries')}
                                </div>
                                <BSPagination className="mb-0">
                                    {renderPaginationItems()}
                                </BSPagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}